import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ConfirmDialogComponent, ConfirmDialogData } from "@cotecna/dialogs";
import {
  FormReturnType,
  NaturalFormConfig,
  NaturalFormFlavour,
  NaturalFormStyle,
  Section,
} from "@cotecna/dynamic-view-engine";
import { ActionElement } from "@cotecna/material";
import { CotecnaUserService } from "@cotecna/session";
import {
  DynamicFormDialogComponent,
  DynamicFormDialogConfig,
  DynamicFormResult,
} from "src/app/form-dialog/components/natural-form-dialog/dynamic-form-dialog.component";
import {
  INSPECTOR_ROLE,
  INVALID_ITEM_QUANTITY_LARGE_MESSAGE,
  INVALID_ITEM_QUANTITY_TITLE,
  ITEM_CREATED_SAME_NAME_ERROR_MESSAGE,
  ITEM_CREATED_SAME_NAME_ERROR_TITLE,
  SERVICE_PROVIDER_INSPECTOR_ROLE,
  UNDERSTOOD_ACTION,
  WARNING_ICON,
} from "src/app/inspection/constants/constants";
import { ImportDialogComponent } from "src/app/inspection/dialogs/import-dialog/import-dialog.component";
import { ItemSectionsDialogComponent } from "src/app/inspection/dialogs/item-sections-dialog/item-sections-dialog.component";
import { LoadingComponent } from "src/app/inspection/dialogs/loading/loading.component";
import { LoadingDialogData } from "src/app/inspection/dialogs/loading/loading-dialog-data";
import { Item } from "src/app/inspection/model/item";
import { ImportItemsService } from "src/app/inspection/services/import-items.service";
import { InspectionFormService } from "src/app/inspection/services/inspection-form-service/inspection-form.service";
import { ScreenInfo } from "src/app/settings/models/screen-info";
import {
  DynamicFormService,
  NaturalFormConverterResult,
} from "src/app/shared/services/dynamic-form/dynamic-form.service";

import {
  ADD_ITEM_LABEL,
  ADD_MULTIPLE_LABEL,
  IMPORT_ITEMS_LABEL,
  MAX_NUM_ITEMS_ERROR,
} from "../../constants/constants";
import {
  NEW_MULTIPLE_PRODUCTS_CONTROLS,
  NEW_MULTIPLE_PRODUCTS_FORM,
} from "../../static-forms/new-multiple-products-form";
import {
  NEW_PRODUCT_CONTROLS,
  NEW_PRODUCT_FORM,
} from "../../static-forms/new-product-form";
import {
  IMPORT_ITEMS_CONTROLS,
  IMPORT_ITEMS_SECTIONS,
} from "./import-items-fields";

@Component({
  selector: "im-item-button",
  templateUrl: "./item-button.component.html",
  styleUrls: ["./item-button.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ItemButtonComponent implements OnInit {
  @Input()
  public defaultFormId: string;

  @Input()
  public allowToDisableSections: boolean;

  @Input()
  public items: Item[] = [];

  @Input()
  public showSectionsDialog: boolean;

  @Output()
  public itemsSelected: EventEmitter<Item[]> = new EventEmitter<Item[]>();

  @Input()
  public inspectionId: string;

  public speedDialActions = [
    {
      icon: "add_box",
      name: ADD_ITEM_LABEL,
      text: ADD_ITEM_LABEL,
      tooltip: ADD_ITEM_LABEL,
    },
    {
      icon: "widgets",
      name: ADD_MULTIPLE_LABEL,
      text: ADD_MULTIPLE_LABEL,
      tooltip: ADD_MULTIPLE_LABEL,
    },
  ] as ActionElement[];

  public isVisible: boolean = false;

  private formScreens: ScreenInfo[] = [];
  private newProductForm: Section[] = [];
  private newProductControls: any[] = [];
  private newMultipleProductForm: Section[] = [];
  private newMultipleProductControls: any[] = [];
  private importItemSections: Section[];
  private importItemControls: any[];
  private readonly MAX_NUMBER_OF_ITEMS = 500;

  constructor(
    private dialog: MatDialog,
    private inspectionFormService: InspectionFormService,
    private importItemsService: ImportItemsService,
    private snackBar: MatSnackBar,
    private dynamicFormService: DynamicFormService,
    private cotecnaUserService: CotecnaUserService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  async ngOnInit(): Promise<void> {
    this.getSpeeDialActions();
    await this.initItemForms();
    this.getImportFields();
  }

  public onFabSpeedDialSelectedAction(actionElement: ActionElement): void {
    const actionName = actionElement.name;
    switch (actionName) {
      case ADD_ITEM_LABEL:
        this.addItem();
        break;
      case ADD_MULTIPLE_LABEL:
        this.addMultipleItems();
        break;
      case IMPORT_ITEMS_LABEL:
        this.importItems();
        break;
    }
  }

  private getSpeeDialActions(): void {
    if (!this.isOnlyInspector()) {
      this.speedDialActions.push({
        icon: "publish",
        name: IMPORT_ITEMS_LABEL,
        text: IMPORT_ITEMS_LABEL,
        tooltip: IMPORT_ITEMS_LABEL,
      });
    }
  }

  private getImportFields(): void {
    IMPORT_ITEMS_CONTROLS.find((c) => c.id == "formId").options =
      this.formScreens.map((f) => {
        return { id: f.id, name: f.fullName };
      });

    this.importItemSections = IMPORT_ITEMS_SECTIONS;
    this.importItemControls = IMPORT_ITEMS_CONTROLS;
  }

  private async initItemForms(): Promise<void> {
    try {
      this.newProductForm = JSON.parse(JSON.stringify(NEW_PRODUCT_FORM));
      this.newProductControls = JSON.parse(
        JSON.stringify(NEW_PRODUCT_CONTROLS),
      );
      this.newMultipleProductForm = JSON.parse(
        JSON.stringify(NEW_MULTIPLE_PRODUCTS_FORM),
      );
      this.newMultipleProductControls = JSON.parse(
        JSON.stringify(NEW_MULTIPLE_PRODUCTS_CONTROLS),
      );
      await this.getFormsOptions();
    } catch (err) {
      this.snackBar.open(
        "It is not possible to get the items form. Try again later.",
        "DISMISS",
      );
    } finally {
      this.isVisible = true;
      this.changeDetectorRef.detectChanges();
    }
  }

  private async addItem(previousValues?: any) {
    this.overrideValuesInAddItemDialog(
      this.newProductForm,
      this.newProductControls,
      previousValues,
    );

    const dialogResult = await this.openNaturalFormDialog({
      icon: "add_box",
      title: "Add item",
      formConfig: this.getFormConfig(),
      sections: JSON.parse(JSON.stringify(this.newProductForm)),
      controls: JSON.parse(JSON.stringify(this.newProductControls)),
      confirmButtonText: "ADD",
      cancelButtonText: "CANCEL",
      allowConfirmWithInvalidFrom: false,
    } as DynamicFormDialogConfig);
    const data = dialogResult.data;
    if (!this.dynamicFormService.hasInvalidQuantity(data.quantity)) {
      if (dialogResult?.isValid && this.showSectionsDialog) {
        await this.selectItemSections(data);
      } else {
        const itemForm = await this.inspectionFormService.getDynamicFormById(
          data.form,
        );
        const item = this.createItem({ ...data, order: -1 }, itemForm);
        if (item) this.itemsSelected.emit([item]);
      }
    } else {
      this.openConfirmDialog(
        INVALID_ITEM_QUANTITY_TITLE,
        INVALID_ITEM_QUANTITY_LARGE_MESSAGE,
        WARNING_ICON,
        UNDERSTOOD_ACTION,
      );
    }
    await this.initItemForms();
  }

  private overrideValuesInAddItemDialog(
    form: Section[],
    control: any[],
    previousValues?: any,
  ) {
    if (previousValues?.name) {
      const nameField = form[0].fields.find((field) => field.id === "name");
      nameField.value = previousValues.name;
      nameField.valid = true;
    }
    if (previousValues?.type) {
      const typeField = form[0].fields.find((field) => field.id === "type");
      typeField.value = previousValues.type;
      typeField.readableValue =
        control
          .find((field) => field.id === "type")
          .options.find((item) => item.id == typeField.value)?.name ?? null;
      typeField.valid = true;
    }
    if (previousValues?.amount) {
      const amountField = form[0].fields.find((field) => field.id === "amount");
      amountField.value = previousValues.amount;
      amountField.valid = true;
    }
    if (previousValues?.quantity) {
      const quantiyField = form[0].fields.find(
        (field) => field.id === "quantity",
      );
      quantiyField.value = previousValues.quantity;
      quantiyField.valid = true;
    }
    if (previousValues?.unit) {
      const unitField = form[0].fields.find((field) => field.id === "unit");
      unitField.value = previousValues.unit;
      unitField.readableValue =
        control
          .find((field) => field.id === "unit")
          .options.find((item) => item.id == unitField.value)?.name ?? null;
      unitField.valid = true;
    }
    if (previousValues?.form) {
      const formField = form[0].fields.find((field) => field.id === "form");
      formField.value = previousValues.form;
      formField.readableValue =
        control
          .find((field) => field.id === "form")
          .options.find((item) => item.id == formField.value)?.name ?? null;
      formField.valid = true;
    }
  }

  private async addMultipleItems(previousValues?: any) {
    this.overrideValuesInAddItemDialog(
      this.newMultipleProductForm,
      this.newMultipleProductControls,
      previousValues,
    );

    const dialogResult = await this.openNaturalFormDialog({
      icon: "widgets",
      title: "Add multiple items",
      formConfig: this.getFormConfig(),
      sections: this.newMultipleProductForm,
      controls: this.newMultipleProductControls,
      confirmButtonText: "ADD",
      cancelButtonText: "CANCEL",
      allowConfirmWithInvalidFrom: false,
    } as DynamicFormDialogConfig);

    if (dialogResult?.isValid) {
      const data = dialogResult.data;
      if (!this.dynamicFormService.hasInvalidQuantity(data.quantity)) {
        if (data.amount > this.MAX_NUMBER_OF_ITEMS)
          this.snackBar.open(MAX_NUM_ITEMS_ERROR, "", {
            duration: 3000,
            data: { color: "warn" },
          });
        else if (this.showSectionsDialog) {
          this.selectMultipleItemSections(data);
        } else {
          const itemForm = await this.inspectionFormService.getDynamicFormById(
            data.form,
          );
          const items = Array.from({ length: data.amount })
            .map((_, index) => {
              return {
                order: index + 1,
                name: `${data.type} ${this.getLastItemOrderByType(data.type) + (index + 1)}`,
                form: data.form,
                quantity: data.quantity,
                unit: data.unit,
              };
            })
            .map((itemSelection) => this.createItem(itemSelection, itemForm));
          this.itemsSelected.emit(items);
        }
      } else {
        this.openConfirmDialog(
          INVALID_ITEM_QUANTITY_TITLE,
          INVALID_ITEM_QUANTITY_LARGE_MESSAGE,
          WARNING_ICON,
          UNDERSTOOD_ACTION,
        );
      }
      await this.initItemForms();
    }
  }

  private async selectItemSections(data: any): Promise<void> {
    let itemForm = await this.inspectionFormService.getDynamicFormById(
      data.form,
    );

    // DISABLE SECTIONS FORM -> GENERATE CUSTOM FORM
    if (this.allowToDisableSections) {
      const sectionDialogResult = this.dialog.open(
        ItemSectionsDialogComponent,
        {
          data: {
            sectionForm: itemForm,
          },
        },
      );
      const result = await sectionDialogResult?.afterClosed().toPromise();
      if (result == null) {
        sectionDialogResult.close();
        return;
      }

      if (result) {
        if (result.option == "back") {
          this.addItem(data);
          return;
        } else {
          itemForm = result.data;
        }
      }
    }

    // CREATE ITEM WITH FORM
    const item = this.createItem({ ...data, order: -1 }, itemForm);
    if (item) {
      this.itemsSelected.emit([item]);
    }
  }

  private async selectMultipleItemSections(data: any): Promise<void> {
    let mutipleItemForm = await this.inspectionFormService.getDynamicFormById(
      data.form,
    );

    // DISABLE SECTIONS FORM -> GENERATE CUSTOM FORM
    if (this.allowToDisableSections) {
      const sectionDialogResult = this.dialog.open(
        ItemSectionsDialogComponent,
        {
          data: {
            sectionForm: mutipleItemForm,
          },
        },
      );
      const result = await sectionDialogResult?.afterClosed().toPromise();

      if (result == null) {
        sectionDialogResult.close();
        return;
      }

      if (result) {
        if (result.option == "back") {
          this.addMultipleItems(data);
          return;
        } else {
          mutipleItemForm = result.data;
        }
      }
    }

    // CREATE ITEM WITH FORM
    const items = Array.from({ length: data.amount })
      .map((_, index) => {
        return {
          order: index + 1,
          name: `${data.type} ${this.getLastItemOrderByType(data.type) + (index + 1)}`,
          form: data.form,
          quantity: data.quantity,
          unit: data.unit,
        };
      })
      .map((itemSelection) => this.createItem(itemSelection, mutipleItemForm));
    this.itemsSelected.emit(items);
  }

  private createItem(
    itemSelection: ItemSelection,
    form: NaturalFormConverterResult,
  ): Item {
    const itemForm = JSON.parse(JSON.stringify(form));
    this.dynamicFormService.setEmptyForm(itemForm.sections);
    if (this.hasItemWithSameName(itemSelection.name.trim())) {
      this.openConfirmDialog(
        ITEM_CREATED_SAME_NAME_ERROR_TITLE,
        ITEM_CREATED_SAME_NAME_ERROR_MESSAGE,
        WARNING_ICON,
        UNDERSTOOD_ACTION,
      );
      return;
    }
    if (this.dynamicFormService.hasInvalidQuantity(itemSelection.quantity)) {
      this.openConfirmDialog(
        INVALID_ITEM_QUANTITY_TITLE,
        INVALID_ITEM_QUANTITY_LARGE_MESSAGE,
        WARNING_ICON,
        UNDERSTOOD_ACTION,
      );
      return;
    }
    return {
      order: itemSelection.order,
      name: itemSelection.name,
      quantity: itemSelection.quantity,
      unit: itemSelection.unit,
      formId: itemSelection.form,
      form: {
        sections: itemForm.sections,
        controls: itemForm.controls,
      },
      values: [],
      search: "",
      isCompleted: false,
    } as any;
  }

  private async importItems() {
    const result = await this.dialog
      .open(ImportDialogComponent, {
        autoFocus: false,
        disableClose: false,
        data: {
          title: "Import Items",
          sections: this.importItemSections,
          controls: this.importItemControls,
          defaultFormId: this.defaultFormId,
        },
        height: "50vh",
        panelClass: ["import-data-dialog"],
      })
      .afterClosed()
      .toPromise();

    if (result) {
      try {
        const items = await this.importItemsService.importItems(
          result.data[0].value,
          result.file,
        );
        for (const item of items) {
          await this.dynamicFormService.setFormValues(
            item.values,
            item.form.sections,
            item.form.controls,
          );
          item.formId = result.data[0].value;
        }
        this.itemsSelected.emit(items);
        await this.initItemForms();
      } catch (err) {
        const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
          data: <ConfirmDialogData>{
            icon: "error",
            okButton: {
              text: "import items",
              color: "primary",
            },
            cancelButton: {
              text: "cancel",
            },
            title: "Error importing items",
            message: err,
          },
          panelClass: "error-import-items",
          autoFocus: false,
        });
        const confirmDialogResult = await confirmDialog
          .afterClosed()
          .toPromise();
        if (confirmDialogResult) {
          this.importItems();
        }
      }
    }
  }

  private getFormConfig() {
    return {
      displayMode: NaturalFormStyle.material,
      flavor: NaturalFormFlavour.informative,
      notifyFieldChange: false,
      returnType: FormReturnType.entity,
    } as NaturalFormConfig;
  }

  private async openNaturalFormDialog(
    dialogConfig: DynamicFormDialogConfig,
  ): Promise<DynamicFormResult> {
    const dialogRef = this.dialog.open(DynamicFormDialogComponent, {
      autoFocus: false,
      disableClose: false,
      data: dialogConfig,
      panelClass: ["creation-natural-form-dialog"],
    });

    const result: DynamicFormResult = await dialogRef.afterClosed().toPromise();
    return result;
  }

  private async getFormsOptions() {
    if (this.formScreens.length == 0) {
      this.formScreens = await this.inspectionFormService
        .getAllFormsForUserAccount(this.inspectionId)
        .pipe()
        .toPromise();
    }

    const forms = this.formScreens.map((x) => ({ id: x.id, name: x.fullName }));
    this.newProductControls.find((control) => control.id === "form").options = [
      ...forms,
    ];
    this.setFormFieldByDefault(this.newProductForm, this.formScreens);

    this.newMultipleProductControls.find(
      (control) => control.id === "form",
    ).options = [...forms];
    this.setFormFieldByDefault(this.newMultipleProductForm, this.formScreens);
  }

  private setFormFieldByDefault(currentForm: any[], formScreens: ScreenInfo[]) {
    //applying presets
    if (this.defaultFormId) {
      const formField = currentForm[0].fields.find(
        (field) => field.id === "form",
      );
      if (formField) {
        formField.value = this.defaultFormId;
        formField.readableValue = formScreens.find(
          (screen) => screen.id === formField.value,
        )?.fullName;
        formField.valid = true;
      }
    }
  }

  private getLastItemOrderByType(type: string): number {
    const previousItems = this.items.map((item) => item.name);
    const items = (previousItems as any).filter((item) =>
      item.startsWith(type),
    );
    const withSpace = items.filter((word) => word.includes(" "));

    if (items.length > 0 && withSpace.length > 0) {
      const value = withSpace.at(-1).charAt(withSpace.at(-1).length - 1);
      return Number(value[0]);
    }
    return items?.length;
  }

  private openLoadingDialog(message: string): MatDialogRef<any> {
    return this.dialog.open(LoadingComponent, {
      data: { loadingText: message } as LoadingDialogData,
      autoFocus: false,
      disableClose: false,
    });
  }

  private openConfirmDialog(
    title: string,
    message: string,
    icon: string,
    action: string,
  ): void {
    this.dialog.open(ConfirmDialogComponent, {
      autoFocus: false,
      panelClass: "inspection-action-dialog",
      data: <ConfirmDialogData>{
        icon,
        okButton: { text: action, color: "primary" },
        cancelButton: { text: "" },
        title,
        message,
      },
    });
  }

  private hasItemWithSameName(itemName: string): boolean {
    return this.items?.length
      ? this.items?.some(
          (item) => item.name.toLowerCase() === itemName.toLowerCase(),
        )
      : false;
  }

  public isOnlyInspector(): boolean {
    const roles = this.cotecnaUserService.getUser()?.roles;
    return (
      roles?.length === 1 &&
      (roles[0] === INSPECTOR_ROLE ||
        roles[0] === SERVICE_PROVIDER_INSPECTOR_ROLE)
    );
  }
}

interface ItemSelection {
  order: number;
  name: string;
  quantity: number;
  unit: string;
  form: string;
}
